import type { User } from '../../../authentication/types/types.auth';
import { Lock, Public, Save } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  Paper,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import { ColorPicker } from '@shared/components/ColorPicker';
import { commonCards, gradients } from '@shared/theme/patterns';
import { ProfileFormField } from './ProfileFormField';

const AVATAR_COLORS = [
  { color: '#c05a3a', name: 'Ocean Blue' },
  { color: '#d4825e', name: 'Sky Blue' },
  { color: '#2E5AAC', name: 'Navy Blue' },
  { color: '#8B5CF6', name: 'Purple' },
  { color: '#EC4899', name: 'Pink' },
  { color: '#F59E0B', name: 'Amber' },
  { color: '#10B981', name: 'Emerald' },
  { color: '#EF4444', name: 'Red' },
  { color: '#64748B', name: 'Slate' },
  { color: '#06B6D4', name: 'Cyan' },
  { color: '#F97316', name: 'Orange' },
  { color: '#84CC16', name: 'Lime' },
  { color: '#A855F7', name: 'Violet' },
  { color: '#14B8A6', name: 'Teal' },
  { color: '#F43F5E', name: 'Rose' },
  { color: '#8B5A3C', name: 'Brown' },
];

interface ProfileFormData {
  avatarColor: string;
  firstName: string;
  hourlyRate: string;
  lastName: string;
  username: string;
  email: string;
  company: string;
  positionTitle: string;
  industry: string;
  companySize: string;
  tagline: string;
  showDealList: boolean;
}

interface ProfileDetailsCardProps {
  user: User;
  isEditing: boolean;
  saving: boolean;
  formData: ProfileFormData;
  onFieldChange: (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  onColorChange: (color: string) => void;
  onToggleChange: (field: string) => (value: boolean) => void;
  onSave: () => void;
  onCancel: () => void;
}

export const ProfileDetailsCard: React.FC<ProfileDetailsCardProps> = ({
  user,
  isEditing,
  saving,
  formData,
  onFieldChange,
  onColorChange,
  onToggleChange,
  onSave,
  onCancel,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.5 }}
    >
      <Paper sx={{ ...commonCards.bordered, p: 4, background: 'linear-gradient(to bottom, #ffffff 0%, #f8faff 100%)' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              background: gradients.primary,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: '1.5rem',
            }}
          >
            {isEditing ? 'Edit Profile Information' : 'Profile Information'}
          </Typography>

          {isEditing && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={onCancel}
                variant="outlined"
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={onSave}
                variant="contained"
                disabled={saving}
                startIcon={<Save />}
                data-testid="save-profile-button"
                sx={{
                  'background': gradients.primary,
                  'borderRadius': 2,
                  'textTransform': 'none',
                  'boxShadow': '0 4px 12px rgba(192, 90, 58, 0.25)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(192, 90, 58, 0.35)',
                    transform: 'translateY(-2px)',
                  },
                  'transition': 'all 0.3s ease',
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {/* Public Information Section */}
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 1,
                pb: 2,
                borderBottom: '2px solid',
                borderColor: 'primary.main',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, rgba(192, 90, 58, 0.1) 0%, rgba(212, 130, 94, 0.1) 100%)',
                }}
              >
                <Public color="primary" sx={{ fontSize: 24 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.15rem', color: 'text.primary' }}>
                Public Information
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, fontStyle: 'italic' }}>
              This information will be displayed on your reviews and in the user directory
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Username and Tag Line Row */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <ProfileFormField
                    label="Username"
                    value={formData.username}
                    displayValue={user.username}
                    isEditing={isEditing}
                    onChange={onFieldChange('username')}
                    placeholder="e.g., john_doe"
                    dataTestId="username-input"
                  />
                </Box>

                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <ProfileFormField
                    label="Tag Line"
                    value={formData.tagline}
                    displayValue={user.tagLine}
                    isEditing={isEditing}
                    onChange={onFieldChange('tagline')}
                    placeholder="e.g., Closing major accounts in data analytics since 2018"
                    dataTestId="tagline-input"
                    renderDisplay={() => (
                      <Typography variant="body1" sx={{ fontWeight: 500, fontStyle: 'italic' }}>
                        {user.tagLine || '—'}
                      </Typography>
                    )}
                  />
                </Box>
              </Box>

              {/* Avatar Color and Hourly Rate Row */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  {isEditing
                    ? (
                        <ColorPicker
                          value={formData.avatarColor}
                          onChange={onColorChange}
                          label="Avatar Color"
                        />
                      )
                    : (
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              mb: 1.5,
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              letterSpacing: '0.05em',
                              textTransform: 'uppercase',
                              color: 'text.secondary',
                            }}
                          >
                            Avatar Color
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              px: 2.5,
                              py: 2,
                              borderRadius: 2.5,
                              backgroundColor: 'rgba(192, 90, 58, 0.03)',
                              border: '1px solid',
                              borderColor: 'rgba(192, 90, 58, 0.08)',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: 1,
                                backgroundColor: user.avatarColor || '#c05a3a',
                                border: '2px solid white',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                flexShrink: 0,
                              }}
                            />
                            <Typography variant="body1" sx={{ fontWeight: 400, fontSize: '0.9375rem', color: 'text.primary' }}>
                              {AVATAR_COLORS.find(c => c.color === (user.avatarColor || '#c05a3a'))?.name || 'Custom'}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                </Box>

                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <ProfileFormField
                    label="Hourly Rate (USD)"
                    value={formData.hourlyRate}
                    displayValue={user.hourlyRate ? `$${user.hourlyRate}` : '—'}
                    isEditing={isEditing}
                    onChange={onFieldChange('hourlyRate')}
                    placeholder="e.g., 150"
                    dataTestId="hourly-rate-input"
                    type="number"
                    helperText="We recommend keeping it around $300 USD/hour or less to maximize your chances of connecting with buyers."
                  />
                </Box>
              </Box>

              {/* Show Deal List Toggle */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1.5,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  Show Deal List
                </Typography>
                <Tooltip
                  title={!isEditing ? 'To change this preference, click the edit button first' : ''}
                  arrow
                  placement="top"
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2.5,
                      py: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'rgba(192, 90, 58, 0.02)',
                      cursor: !isEditing ? 'not-allowed' : 'default',
                      opacity: !isEditing ? 0.7 : 1,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Display deal list on profile
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {formData.showDealList
                          ? 'Users will be able to see a consolidated list of your published deals reviews'
                          : 'Users will not be able to see a consolidated list of your published deals reviews, but they will still see individual reviews'}
                      </Typography>
                    </Box>
                    <Switch
                      checked={formData.showDealList}
                      onChange={e => onToggleChange('showDealList')(e.target.checked)}
                      disabled={!isEditing}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'primary.main',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: 'primary.main',
                        },
                      }}
                    />
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* Private Information Section */}
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 1,
                pb: 2,
                borderBottom: '2px solid',
                borderColor: 'primary.main',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, rgba(192, 90, 58, 0.1) 0%, rgba(212, 130, 94, 0.1) 100%)',
                }}
              >
                <Lock color="primary" sx={{ fontSize: 24 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.15rem', color: 'text.primary' }}>
                Private Information
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, fontStyle: 'italic' }}>
              This information will always be private. No one else can see it.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* First Name and Last Name Row */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <ProfileFormField
                    label="First Name"
                    value={formData.firstName}
                    displayValue={user.firstName}
                    isEditing={false}
                    onChange={onFieldChange('firstName')}
                  />
                </Box>

                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <ProfileFormField
                    label="Last Name"
                    value={formData.lastName}
                    displayValue={user.lastName}
                    isEditing={false}
                    onChange={onFieldChange('lastName')}
                  />
                </Box>
              </Box>

              {/* Email - Full Width */}
              <ProfileFormField
                label="Email"
                value={formData.email}
                displayValue={user.email}
                isEditing={false}
                onChange={onFieldChange('email')}
                type="email"
              />

              {/* Company and Position Row */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <ProfileFormField
                    label="Current Company"
                    value={formData.company}
                    displayValue={user.company}
                    isEditing={isEditing}
                    onChange={onFieldChange('company')}
                    dataTestId="company-input"
                  />
                </Box>

                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <ProfileFormField
                    label="Position Title"
                    value={formData.positionTitle}
                    displayValue={user.positionTitle}
                    isEditing={isEditing}
                    onChange={onFieldChange('positionTitle')}
                    dataTestId="position-input"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </motion.div>
  );
};
